import type { ClaimRow } from '../types/claims.types';

/** The first and last day, inclusive, that the loaded claims actually cover. ISO `YYYY-MM-DD`. */
export interface DateBounds {
  readonly min: string;
  readonly max: string;
}

/** One window per date filter. Null when the loaded claims carry no date of that kind at all. */
export interface ClaimDateBounds {
  readonly service: DateBounds | null;
  readonly payment: DateBounds | null;
}

/** No claims loaded yet, so nothing constrains the pickers and only the range rules apply. */
export const NO_DATE_BOUNDS: ClaimDateBounds = { service: null, payment: null };

/**
 * The date windows the filter panel may offer (PP-011).
 *
 * Derived from the claims themselves rather than from fixed calendar limits, because the claims are
 * already loaded and client-side: a day outside this window cannot match a row no matter what else is
 * filtered, so offering it is offering an empty result. It is also what catches the real defect — typing
 * into the year segment of `<input type="date">` produces year 0001 on the way to a real year, and
 * nothing previously refused it.
 *
 * Read from the claim AND its lines, because filtering matches a claim when any of them matches
 * (`matchesView`). The live payload often carries the payment date on the lines alone, so bounds taken
 * from the header would leave the Payment Date filter with no window at all.
 */
export function claimDateBounds(claims: readonly ClaimRow[]): ClaimDateBounds {
  const rows = claims.flatMap((claim) => [claim, ...claim.lines]);

  return {
    service: boundsOf(rows.flatMap((row) => [row.serviceDateFrom, row.serviceDateTo])),
    payment: boundsOf(rows.map((row) => row.paymentDate)),
  };
}

/** True when a filled-in range lies entirely outside the window the loaded claims cover. */
export function missesWindow(from: string, to: string, bounds: DateBounds | null): boolean {
  if (bounds === null || from.length === 0 || to.length === 0) {
    return false;
  }

  return to < bounds.min || from > bounds.max;
}

const ISO_DAY_LENGTH = 10;

function boundsOf(values: readonly (string | null)[]): DateBounds | null {
  let min: string | null = null;
  let max: string | null = null;

  for (const value of values) {
    if (value === null || value.length < ISO_DAY_LENGTH) {
      continue;
    }

    const day = value.slice(0, ISO_DAY_LENGTH);
    if (min === null || day < min) {
      min = day;
    }
    if (max === null || day > max) {
      max = day;
    }
  }

  return min === null || max === null ? null : { min, max };
}
